import { z } from 'zod';

export const channelApiBaseUrls = {
  dev: 'https://dev.w.trio.am/api/',
  stage: 'https://stage.w.trio.am/api/',
  prod: 'https://w.trio.am/api/',
} as const;

const appEnvironmentSchema = z.enum(['local', 'dev', 'stage', 'prod', 'test']);
export type AppEnvironment = z.infer<typeof appEnvironmentSchema>;

export const httpUrlSchema = z.string().refine((value) => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
}, 'Expected an HTTP URL without credentials, query, or fragment');

export const runtimeConfigSchema = z
  .strictObject({
    schemaVersion: z.literal(1),
    appEnv: appEnvironmentSchema,
    apiBaseUrl: z.union([z.literal('/api'), httpUrlSchema]),
    xOrigin: z
      .string()
      .regex(/^[a-zA-Z0-9.-]+(?::\d+)?$/u)
      .optional(),
  })
  .superRefine((configuration, context) => {
    const { appEnv, apiBaseUrl, xOrigin } = configuration;
    if (appEnv === 'local' || appEnv === 'test') {
      return;
    }

    const expectedApiBaseUrl = channelApiBaseUrls[appEnv];
    if (apiBaseUrl !== '/api' && apiBaseUrl !== expectedApiBaseUrl) {
      context.addIssue({
        code: 'custom',
        path: ['apiBaseUrl'],
        message: 'API URL must match the application channel',
      });
    }
    if (xOrigin !== undefined && xOrigin !== new URL(expectedApiBaseUrl).host) {
      context.addIssue({
        code: 'custom',
        path: ['xOrigin'],
        message: 'X-Origin must match the application channel',
      });
    }
  });

export type RuntimeConfig = Readonly<z.infer<typeof runtimeConfigSchema>>;

export const parseRuntimeConfig = (source: unknown): RuntimeConfig =>
  Object.freeze(runtimeConfigSchema.parse(source));
